/**
 * ==========================================
 * DESIGN SYSTEM - THEME TOKENS
 * ==========================================
 *
 * Central source of truth for all design tokens in the Lamma platform.
 * This file defines colors, typography, spacing, and other design primitives
 * that create a consistent, professional visual language across all elements.
 *
 * ARCHITECTURE:
 * - Three-tier color system: Platform → Semantic → Component
 * - Future-proof for theme switching (dark mode, high contrast, etc.)
 * - Single point of control for all visual styling
 *
 * USAGE:
 * - Import these tokens in tailwind.config.js
 * - Reference in components via Tailwind classes
 * - DO NOT hardcode colors/sizes in components

 */

// ==========================================
// COLOR SYSTEM HIERARCHY
// ==========================================
/**
 * THREE-TIER COLOR SYSTEM:
 *
 * Level 1 - Platform Colors (Brand Identity)
 *   Purpose: Define the app's visual DNA
 *   Use for: Navigation, logos, primary CTAs, brand elements
 *   Change frequency: Rare (only during rebranding)
 *
 * Level 2 - Semantic Colors (Universal Meaning)
 *   Purpose: Communicate meaning across all contexts
 *   Use for: Success/error states, warnings, informational messages
 *   Change frequency: Occasional (when refining UX feedback)
 *
 * Level 3 - Component Colors (Specific UI Elements)
 *   Purpose: Fine-grained control for specific button types, badges, etc.
 *   Use for: Hint buttons, explanation modals, specific interactions
 *   Change frequency: Frequent (iterating on individual components)
 */

/**
 * Default Theme Configuration
 *
 * Based on "Soft Modern" aesthetic (Framer, Arc Browser, Raycast)
 * - Friendly, approachable colors
 * - Soft edges, gentle contrasts
 * - Professional but not corporate
 */
export const THEME_DEFAULT = {
  // ==========================================
  // LEVEL 1: PLATFORM COLORS (Brand Identity)
  // ==========================================

  /**
   * Primary Color - Main Brand Identity
   *
   * Usage: Main navigation, primary CTAs, focus states, brand elements
   * Examples: "Submit Answer", "Create Lesson", Active navigation items
   *
   * Color: Professional Blue (#1349ad)
   * Psychology: Trust, intelligence, education, stability
   */
  primary: {
    DEFAULT: '#1349ad',
    50: '#e6eeff',   // Very light tint for backgrounds
    100: '#ccdcff',  // Light tint for hover states
    200: '#99b9ff',
    300: '#6696ff',
    400: '#3373ff',
    500: '#1349ad',  // Main brand color
    600: '#0f3a8a',  // Darker for hover
    700: '#0c2d6b',  // Darkest for active states
    800: '#081f4a',
    900: '#051129',

    // Semantic aliases for common uses
    hover: '#0f3a8a',      // Hover state
    light: '#e6eeff',      // Light backgrounds
    dark: '#0c2d6b',       // Dark text
  },

  /**
   * Gray Scale - Neutrals
   *
   * Usage: Text hierarchy, borders, backgrounds, disabled states
   * Examples: Body text, dividers, card backgrounds, disabled buttons
   */
  gray: {
    50: '#fafafa',   // Lightest - backgrounds
    100: '#f5f5f5',  // Very light - secondary backgrounds
    200: '#e5e5e5',  // Light borders
    300: '#d4d4d4',  // Medium borders
    400: '#a3a3a3',  // Icons, placeholders
    500: '#737373',  // Secondary text
    600: '#525252',  // Body text
    700: '#404040',  // Headings
    800: '#262626',  // Strong emphasis
    900: '#171717',  // Maximum contrast
  },

  /**
   * Text Color Hierarchy
   *
   * Usage: Establishing visual hierarchy in typography
   */
  text: {
    primary: '#171717',      // Main body text, headings
    secondary: '#525252',    // Less important text, labels
    tertiary: '#737373',     // Captions, helper text
    quaternary: '#a3a3a3',   // Placeholders, disabled text
    inverse: '#ffffff',      // Text on dark backgrounds
  },

  /**
   * Background Colors - Semantic Naming
   *
   * Usage: Layering and depth in the interface
   * Named by PURPOSE, not appearance, for theming flexibility
   */
  background: {
    // Main backgrounds
    app: '#fafafa',          // Main app/page background (warm gray)
    canvas: '#fafafa',       // Activity slide canvas background (warm gray)

    // Surface backgrounds (elevated on app background)
    surface: '#ffffff',      // Cards, modals, panels (white for contrast)
    surfaceHover: '#f5f5f5', // Surface hover state (darker than app bg)

    // Navigation backgrounds
    navigation: '#ffffff',   // Sidebar, topbar navigation (white)
    navigationHover: '#f5f5f5', // Navigation item hover state
    navigationActive: '#e6eeff', // Active navigation item (light primary blue tint)

    // Special backgrounds
    accent: '#f0f9fa',       // Info/highlight backgrounds (legacy cyan tint)
    subtle: '#f5f5f5',       // Disabled states, subtle backgrounds
  },

  /**
   * Border Colors
   *
   * Usage: Separators, outlines, focus rings
   */
  border: {
    light: '#e5e5e5',        // Subtle dividers
    medium: '#d4d4d4',       // Standard borders
    strong: '#a3a3a3',       // Emphasized borders
    focus: '#1349ad',        // Focus rings (uses primary)
  },

  /**
   * Input/Control Colors
   *
   * Usage: Form controls, sliders, input fields
   */
  control: {
    track: '#d4d4d4',        // Slider/progress track background (visible neutral)
    thumb: '#1349ad',        // Slider thumb/handle (uses primary for interactivity)
    border: '#d4d4d4',       // Input borders
    background: '#ffffff',   // Input background
    disabled: '#f5f5f5',     // Disabled state background
  },

  // ==========================================
  // LEVEL 2: SEMANTIC COLORS (Universal Meaning)
  // ==========================================

  /**
   * Feedback Color System - "Soft Modern" Palette
   *
   * These colors communicate universal meaning across ALL elements.
   * When a student sees green, it ALWAYS means success/correct.
   * When a student sees red, it ALWAYS means error/incorrect.
   *
   * Design philosophy: Soft, friendly, approachable
   * - Not aggressive or harsh
   * - Clear but gentle communication
   * - Accessible contrast ratios
   */
  feedback: {
    /**
     * Success Colors - Positive Outcomes
     *
     * Usage: Correct answers, validation passed, completion, achievements
     * Examples: MCQ correct option, Graph criterion met, Game target hit
     *
     * Color: Fresh Green (#51cf66)
     * Psychology: Growth, achievement, correctness, progress
     */
    success: {
      DEFAULT: '#51cf66',       // Main success color
      light: '#d3f9d8',         // Light background for success states
      lighter: '#ebfbee',       // Very light for subtle highlights
      border: '#8ce99a',        // Border color for success boxes
      text: '#2b8a3e',          // Text on light success backgrounds
      dark: '#2f9e44',          // Darker for emphasis
    },

    /**
     * Error Colors - Negative Outcomes
     *
     * Usage: Wrong answers, validation failed, errors, blocked actions
     * Examples: MCQ incorrect option, Graph criterion failed, Game miss
     *
     * Color: Coral Red (#ff6b6b)
     * Psychology: Attention, mistake, needs correction (but not harsh)
     */
    error: {
      DEFAULT: '#ff6b6b',       // Main error color
      light: '#ffe3e3',         // Light background for error states
      lighter: '#fff5f5',       // Very light for subtle highlights
      border: '#ff8787',        // Border color for error boxes
      text: '#c92a2a',          // Text on light error backgrounds
      dark: '#fa5252',          // Darker for emphasis
    },

    /**
     * Info Colors - Informational Content
     *
     * Usage: Hints, tips, informational messages, helper text
     * Examples: Hint buttons/modals, info tooltips, help panels
     *
     * Color: Sky Blue (#74c0fc)
     * Psychology: Helpful, calm, informational, supportive
     */
    info: {
      DEFAULT: '#74c0fc',       // Main info color
      light: '#d0ebff',         // Light background for info states
      lighter: '#e7f5ff',       // Very light for subtle highlights
      border: '#a5d8ff',        // Border color for info boxes
      text: '#1971c2',          // Text on light info backgrounds
      dark: '#4dabf7',          // Darker for emphasis
    },

    /**
     * Warning Colors - Cautionary States
     *
     * Usage: Warnings, partial credit, attention needed, caution
     * Examples: Time running out, incomplete answers, review needed
     *
     * Color: Peach (#ffc078)
     * Psychology: Caution, review needed, gentle warning
     */
    warning: {
      DEFAULT: '#ffc078',       // Main warning color
      light: '#fff4e6',         // Light background for warning states
      lighter: '#fffaf0',       // Very light for subtle highlights
      border: '#ffd8a8',        // Border color for warning boxes
      text: '#e8590c',          // Text on light warning backgrounds
      dark: '#fd7e14',          // Darker for emphasis
    },

    /**
     * In Progress Colors - Active States
     *
     * Usage: Currently working on, active editing, in progress
     * Examples: Editing indicator, loading states, current activity
     *
     * Color: Soft Purple (#b197fc)
     * Psychology: Active, in progress, focused attention
     */
    inProgress: {
      DEFAULT: '#b197fc',       // Main in-progress color
      light: '#e5dbff',         // Light background
      lighter: '#f3f0ff',       // Very light background
      border: '#d0bfff',        // Border color
      text: '#6741d9',          // Text color
      dark: '#9775fa',          // Darker for emphasis
    },

    /**
     * Active/Selected Colors - Current Focus States
     *
     * Usage: Currently selected items, active criterion, focused elements
     * Examples: Selected graph criterion, active slider, current step
     *
     * Color: Uses primary color for brand consistency
     * Psychology: Focused attention, current context, active selection
     */
    active: {
      DEFAULT: '#1349ad',       // Active indicator (uses primary)
      background: '#e6eeff',    // Light background for active items
      backgroundHover: '#ccdcff', // Hover state for active items
      border: '#1349ad',        // Border for active/selected items (uses primary)
      text: '#0c2d6b',          // Text color for active items (dark primary)
      lighter: '#f0f4ff',       // Very light background for subtle highlighting
    },
  },

  // ==========================================
  // LEVEL 3: COMPONENT COLORS (Specific UI)
  // ==========================================

  /**
   * Button-Specific Colors
   *
   * Fine-grained control for specific button types that need
   * their own identity separate from generic primary/secondary.
   */
  button: {
    /**
     * Hint Button Colors
     *
     * Usage: ONLY for hint buttons across all elements
     * Visual: Lightbulb icon + sky blue color
     *
     * Why separate from info: Hints are special - they deserve
     * their own identity for instant recognition.
     */
    hint: {
      bg: '#74c0fc',            // Background color
      bgHover: '#4dabf7',       // Hover state
      bgActive: '#339af0',      // Active/pressed state
      text: '#1971c2',          // Text color
      textHover: '#1864ab',     // Text hover
      border: '#a5d8ff',        // Border color
      icon: '#1971c2',          // Icon color
    },

    /**
     * Explanation Button Colors
     *
     * Usage: ONLY for explanation buttons across all elements
     * Visual: Book icon + soft purple color
     *
     * Why different from hint: Visual differentiation helps students
     * understand hint (help me solve) vs explanation (teach me why).
     */
    explanation: {
      bg: '#b197fc',            // Background color
      bgHover: '#9775fa',       // Hover state
      bgActive: '#845ef7',      // Active/pressed state
      text: '#6741d9',          // Text color
      textHover: '#5f3dc4',     // Text hover
      border: '#d0bfff',        // Border color
      icon: '#6741d9',          // Icon color
    },

    /**
     * Settings Button Colors
     *
     * Usage: Element settings/configuration buttons
     * Visual: Gear icon + neutral gray
     */
    settings: {
      bg: '#f8f9fa',            // Background color
      bgHover: '#e9ecef',       // Hover state
      bgActive: '#dee2e6',      // Active/pressed state
      text: '#495057',          // Text color
      border: '#ced4da',        // Border color
      icon: '#495057',          // Icon color
    },

    /**
     * Submit Button Colors
     *
     * Usage: Answer submission, form submission
     * Visual: Uses primary brand color with extra emphasis
     */
    submit: {
      bg: '#1349ad',            // Uses primary
      bgHover: '#0f3a8a',       // Hover
      bgActive: '#0c2d6b',      // Active
      text: '#ffffff',          // Text
      shadow: 'rgba(19, 73, 173, 0.3)', // Shadow for depth
    },

    /**
     * Retry Button Colors
     *
     * Usage: Try again, reset, retry actions
     * Visual: Neutral but encouraging
     */
    retry: {
      bg: '#f1f3f5',            // Background
      bgHover: '#e9ecef',       // Hover
      bgActive: '#dee2e6',      // Active
      text: '#495057',          // Text
      border: '#ced4da',        // Border
      icon: '#495057',          // Icon
    },

    /**
     * Navigation Button Colors
     *
     * Usage: Next/previous, skip, navigation arrows
     * Visual: Minimal, low emphasis
     */
    navigation: {
      bg: 'transparent',        // Transparent background
      bgHover: '#f8f9fa',       // Hover
      bgActive: '#e9ecef',      // Active
      text: '#868e96',          // Text
      textHover: '#495057',     // Text hover
      icon: '#868e96',          // Icon
      iconHover: '#495057',     // Icon hover
    },

    /**
     * Danger Button Colors
     *
     * Usage: Delete, remove, destructive actions
     * Visual: Red, requires confirmation
     */
    danger: {
      bg: '#fff5f5',            // Light red background
      bgHover: '#ffe3e3',       // Hover
      bgActive: '#ffc9c9',      // Active
      text: '#c92a2a',          // Text
      border: '#ff8787',        // Border
      icon: '#c92a2a',          // Icon
    },

    /**
     * Ghost Button Colors
     *
     * Usage: Cancel, close, low-emphasis actions
     * Visual: Transparent, minimal
     */
    ghost: {
      bg: 'transparent',        // Transparent
      bgHover: '#f8f9fa',       // Hover
      bgActive: '#e9ecef',      // Active
      text: '#868e96',          // Text
      textHover: '#495057',     // Text hover
    },

    /**
     * Primary Button Gradient
     *
     * Usage: Main CTAs (Save, Submit, Create)
     * Visual: Professional blue gradient with depth
     */
    primaryGradient: 'linear-gradient(to bottom right, #1349ad, #0f3a8a)',
    primaryGradientHover: 'linear-gradient(to bottom right, #0f3a8a, #0c2d6b)',

    /**
     * Generate Button Gradient
     *
     * Usage: AI-powered generation buttons ("Generate Lesson", AI features)
     * Visual: Multi-color blue gradient for visual distinction
     */
    generateGradient: 'linear-gradient(to right, #4a7dd9, #1349ad, #0f3a8a)',
    generateGradientHover: 'linear-gradient(to right, #3b6bc8, #0f3a8a, #0a2d6b)',
  },

  /**
   * Modal-Specific Colors
   *
   * Colors for different modal types across the system
   */
  modal: {
    /**
     * Hint Modal Colors
     *
     * Usage: All hint modals (MCQ, Graph, TargetShooting)
     */
    hint: {
      headerBg: '#d0ebff',      // Header background
      headerBorder: '#a5d8ff',  // Header border
      headerText: '#1971c2',    // Header text
      iconColor: '#4dabf7',     // Icon color
    },

    /**
     * Explanation Modal Colors
     *
     * Usage: All explanation modals
     */
    explanation: {
      headerBg: '#e5dbff',      // Header background
      headerBorder: '#d0bfff',  // Header border
      headerText: '#6741d9',    // Header text
      iconColor: '#9775fa',     // Icon color
    },

    /**
     * Settings Modal Colors
     *
     * Usage: Settings panels, configuration modals
     */
    settings: {
      headerBg: '#f8f9fa',      // Header background
      headerBorder: '#e9ecef',  // Header border
      headerText: '#495057',    // Header text
      iconColor: '#868e96',     // Icon color
    },
  },

  // ==========================================
  // TYPOGRAPHY SYSTEM
  // ==========================================

  /**
   * Font Families
   *
   * ABC Monument Grotesk carries Latin; Simpler Pro carries Hebrew and Arabic (fonts.css)
   */
  fontFamily: {
    primary: ['ABC Monument Grotesk', 'Simpler Pro', 'ui-sans-serif', 'system-ui', '-apple-system', 'BlinkMacSystemFont', 'Segoe UI', 'Roboto', 'Helvetica Neue', 'Arial', 'sans-serif'],
    lesson: ['ABC Monument Grotesk', 'Simpler Pro', 'ui-sans-serif', 'system-ui', '-apple-system', 'BlinkMacSystemFont', 'Segoe UI', 'Roboto', 'Helvetica Neue', 'Arial', 'sans-serif'],
    mono: ['JetBrains Mono', 'Fira Code', 'Consolas', 'monospace'],
  },

  /**
   * Font Sizes with Responsive Scaling
   *
   * Uses clamp() for fluid typography that adapts to viewport
   */
  fontSize: {
    xs: {
      size: 'clamp(0.6875rem, 0.65rem + 0.15vw, 0.75rem)',
      lineHeight: '1.25',
    },
    sm: {
      size: 'clamp(0.8125rem, 0.75rem + 0.2vw, 0.875rem)',
      lineHeight: '1.375',
    },
    base: {
      size: 'clamp(0.875rem, 0.825rem + 0.2vw, 0.9375rem)',
      lineHeight: '1.5',
    },
    lg: {
      size: 'clamp(1rem, 0.95rem + 0.25vw, 1.0625rem)',
      lineHeight: '1.5',
    },
    xl: {
      size: 'clamp(1.125rem, 1.05rem + 0.3vw, 1.1875rem)',
      lineHeight: '1.25',
    },
    '2xl': {
      size: 'clamp(1.3125rem, 1.2rem + 0.4vw, 1.4375rem)',
      lineHeight: '1.25',
    },
  },

  /** Slide Font Sizes: fixed slide px for learning elements, never fluid; size on the scaled canvas, phoneSize in the phone slide layout */
  slideFontSize: {
    caption: { size: 16, phoneSize: 15, lineHeight: '1.375' },
    label: { size: 18, phoneSize: 16, lineHeight: '1.375' },
    body: { size: 24, phoneSize: 17, lineHeight: '1.4' },
    prompt: { size: 26, phoneSize: 20, lineHeight: '1.35' },
  },

  /** Phone size of a teacher-set slide px: phonePx * (px / canvasPx) ^ exponent, keeping order while pulling toward phonePx */
  slidePhoneType: {
    canvasPx: 20,
    phonePx: 17,
    exponent: 0.45,
  },

  /**
   * Font Weights
   *
   * Semantic naming for consistent hierarchy
   */
  fontWeight: {
    light: '300',      // Rarely used
    normal: '400',     // Body text
    medium: '500',     // Buttons, emphasized text
    semibold: '600',   // Subheadings, labels
    bold: '700',       // Headings
    extrabold: '800',  // Hero text, major emphasis
  },

  // ==========================================
  // SPACING SYSTEM (8px Grid)
  // ==========================================

  /**
   * Spacing Scale
   *
   * Based on 8px grid for consistent rhythm and alignment
   * All spacing should be multiples of 4px (0.25rem)
   */
  spacing: {
    px: '1px',         // Borders only
    0: '0',
    0.5: '0.125rem',   // 2px - tight spacing
    1: '0.25rem',      // 4px - very compact
    1.5: '0.375rem',   // 6px
    2: '0.5rem',       // 8px - base unit
    2.5: '0.625rem',   // 10px
    3: '0.75rem',      // 12px - common gap
    4: '1rem',         // 16px - standard spacing
    5: '1.25rem',      // 20px
    6: '1.5rem',       // 24px - section spacing
    8: '2rem',         // 32px - large gaps
    10: '2.5rem',      // 40px
    12: '3rem',        // 48px - major sections
  },

  // ==========================================
  // BUTTON SIZE SPECIFICATIONS
  // ==========================================

  /**
   * Button Sizes
   *
   * Consistent sizing across all button variants
   * Includes padding, text size, icon size
   */
  buttonSize: {
    small: {
      padding: '0.375rem 0.625rem',    // py-1.5 px-2.5
      fontSize: '0.75rem',             // text-xs
      iconSize: '0.875rem',            // w-3.5 h-3.5
      gap: '0.375rem',                 // gap-1.5
      height: '1.75rem',               // h-7 (28px)
      minWidth: '1.75rem',             // min-w-7
    },
    medium: {
      padding: '0.5rem 0.75rem',       // py-2 px-3
      fontSize: '0.8125rem',           // ~text-sm compact
      iconSize: '1rem',                // w-4 h-4
      gap: '0.375rem',                 // gap-1.5
      height: '2rem',                  // h-8 (32px)
      minWidth: '2rem',                // min-w-8
    },
    large: {
      padding: '0.625rem 1rem',        // py-2.5 px-4
      fontSize: '0.875rem',            // text-sm
      iconSize: '1.125rem',            // w-4.5 h-4.5
      gap: '0.5rem',                   // gap-2
      height: '2.5rem',                // h-10 (40px)
      minWidth: '2.5rem',              // min-w-10
    },
  },

  // ==========================================
  // BORDER RADIUS
  // ==========================================

  /**
   * Border Radius Scale
   *
   * Soft, modern rounded corners
   */
  borderRadius: {
    none: '0',
    sm: '0.25rem',       // 4px - subtle
    DEFAULT: '0.5rem',   // 8px - standard
    md: '0.5rem',        // 8px - alias
    lg: '0.75rem',       // 12px - cards, buttons
    xl: '1rem',          // 16px - modals
    '2xl': '1.5rem',     // 24px - hero cards
    full: '9999px',      // Pills, circles
  },

  // ==========================================
  // SHADOWS
  // ==========================================

  /**
   * Shadow System
   *
   * Elevation and depth cues
   */
  boxShadow: {
    xs: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    sm: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)',
    DEFAULT: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
    md: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
    lg: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
    xl: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',

    // Colored shadows for emphasis
    primary: '0 10px 15px -3px rgba(19, 73, 173, 0.3)',
    success: '0 10px 15px -3px rgba(81, 207, 102, 0.3)',
    error: '0 10px 15px -3px rgba(255, 107, 107, 0.3)',
  },

  // ==========================================
  // TRANSITIONS
  // ==========================================

  /**
   * Transition Timings
   *
   * Consistent animation speeds
   */
  transitionDuration: {
    fast: '100ms',       // Instant feedback
    DEFAULT: '150ms',    // Standard transitions
    medium: '200ms',     // Smooth transitions
    slow: '300ms',       // Deliberate animations
  },

  /**
   * Easing Functions
   *
   * Natural motion curves
   */
  transitionTiming: {
    DEFAULT: 'cubic-bezier(0.4, 0, 0.2, 1)',        // Tailwind default
    smooth: 'cubic-bezier(0.33, 1, 0.68, 1)',       // Smooth out
    snappy: 'cubic-bezier(0.32, 0, 0.67, 0)',       // Snappy in
    bounce: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)', // Playful bounce
  },
};

/**
 * Type definition for theme structure
 * Enables TypeScript autocomplete and type safety
 */
export type Theme = typeof THEME_DEFAULT;

/**
 * Export default theme as default
 */
export default THEME_DEFAULT;
